import { FormModal } from '@/components/form-modal';
import { FormCheckbox } from '@/components/forms/form-checkbox';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DialogDescription } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/hooks/use-toast';
import { deleteOrg, getOrgDeleteEligibility, getOrgs, invalidateOrgQueries } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

const formSchema = z.object({
  confirmed: z.boolean().default(false),
});

export function DeleteOrg({ orgId, districtName }: { orgId: string; districtName: string }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: eligibility, isPending: isEligibilityPending } = useQuery(getOrgDeleteEligibility(orgId));

  const { mutateAsync: deleteOrgMutate, isPending: isDeletePending } = useMutation({
    mutationFn: deleteOrg,
  });

  const canDelete = eligibility?.canDelete ?? false;
  const isPending = isEligibilityPending || isDeletePending;

  const onSubmit = async ({
    formData,
    closeModal,
  }: {
    formData: z.infer<typeof formSchema>;
    closeModal: () => void;
  }) => {
    if (!canDelete) {
      return;
    }

    if (!formData.confirmed) {
      toast({
        title: 'Please confirm that you understand this district will be removed before continuing.',
      });
      return;
    }

    try {
      await deleteOrgMutate(orgId);
      closeModal();
      queryClient.invalidateQueries(getOrgs());
      invalidateOrgQueries(queryClient);
      navigate({
        to: '/orgs',
        search: {
          pageIndex: 0,
          pageSize: 50,
        },
      });
    } catch (e) {
      const error = airServerErrorFrom(e);
      toast({
        variant: 'destructive',
        title: 'Failed to delete district',
        description: error?.userFacingMessage,
      });
    }
  };

  return (
    <FormModal
      modalTitle="Delete district"
      dataTestId="district-modal-delete"
      trigger={
        <Button type="button" variant="destructive" data-testid="district-button-delete">
          Delete
        </Button>
      }
      onSubmit={onSubmit}
      dialogContentProps={{ className: 'max-w-md' }}
      submitButtonProps={{
        variant: 'destructive',
        children: 'Delete district',
        loading: isPending,
        disabled: isEligibilityPending || !canDelete,
      }}
      formSchema={formSchema}
      confirmBtnLabel="Delete district"
      defaultValues={{ confirmed: false }}
    >
      {({ form }) => (
        <div className="my-6 space-y-4">
          {isEligibilityPending ? (
            <DialogDescription>Checking whether this district can be deleted…</DialogDescription>
          ) : !canDelete && eligibility?.reasons.length ? (
            <Alert variant="destructive">
              <AlertTitle>This district cannot be deleted</AlertTitle>
              <AlertDescription>
                <ul className="list-disc pl-4 space-y-1">
                  {eligibility.reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          ) : (
            <DialogDescription>
              {districtName} will be removed from active and inactive district lists. Schools and the Edlink link are
              not deleted.
            </DialogDescription>
          )}
          {canDelete && (
            <FormCheckbox
              name="confirmed"
              label="I understand this district will be removed from all lists"
              form={form}
              horizontal
            />
          )}
        </div>
      )}
    </FormModal>
  );
}
